import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useForm, SubmitHandler } from 'react-hook-form';
import classNames from 'classnames';
import { TextField, Box, Button, IconButton } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { RiDeleteBinFill, RiArrowLeftLine } from 'react-icons/ri';
import { uniq } from 'lodash';

import { VariantProperty } from 'common/modules/inventory/types';
import { updateLocaleField } from 'common/utils/languageUtils';
import { newFirestoreId } from 'common/utils/dataMigrations';
import ActionDialog from 'common/components/ActionDialog';

import { useTranslation } from 'services/localization/useTranslation';
import * as ShopSelectors from 'selectors/ShopSelectors';

interface ValuesByPropertyId {
	[propKey: string]: string;
}

interface Props {
	properties: VariantProperty[];
	onSave: (prop: ValuesByPropertyId, newVariantProperties?: VariantProperty[]) => void;
	onClose: () => void;
}

type FormValues = {
	priceIncrease: number;
} & ValuesByPropertyId;

const hasDuplicates = (arr: string[]) => uniq(arr).length !== arr.length;

const NewVariantDialog = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const defaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const [isAddOptionsActive, setIsAddOptionsActive] = useState(false);
	const [newOptionNames, setNewOptionNames] = useState<string[]>([]);
	const [newOptions, setNewOptions] = useState<VariantProperty[]>([]);
	const newOptionIds = newOptions.map(({ id }) => id);

	const currentOptionNames = [...props.properties, ...newOptions].map((p) => p.name.def);

	const isDialogPrimaryBtnDisabled = useMemo(() => {
		const nonEmptyNames = newOptionNames.filter((name) => !!name);
		return (
			isAddOptionsActive &&
			(!nonEmptyNames.length ||
				newOptionNames.some((field) => currentOptionNames.includes(field)) ||
				hasDuplicates(nonEmptyNames))
		);
	}, [currentOptionNames, isAddOptionsActive, newOptionNames]);

	const { register, handleSubmit, formState } = useForm<FormValues>({
		mode: 'onChange',
	});

	const { errors } = formState;

	const setAddOptionsActive = () => {
		setIsAddOptionsActive(true);
		setNewOptionNames((existing) => [...existing, '']);
	};

	const cancelAddOptions = () => {
		setIsAddOptionsActive(false);
		setNewOptionNames([]);
	};

	const handleNewOptionNameChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const updatedNames = newOptionNames.map((val, idx) => (idx === index ? e.target.value : val));
		setNewOptionNames(updatedNames);
	};

	const submitNewOptions = () => {
		const nameValues = newOptionNames
			.filter((name) => !!name)
			.map((name) => ({
				id: newFirestoreId(),
				name: updateLocaleField(undefined, name, defaultLanguage),
			}));
		setNewOptions((existing) => [...existing, ...nameValues]);
		setIsAddOptionsActive(false);
		setNewOptionNames([]);
	};

	const handleSubmitForm: SubmitHandler<FormValues> = async (data) => {
		if (Object.keys(formState.errors).length > 0) {
			return;
		}

		const { ...values } = data;
		props.onSave(values, newOptions);
		props.onClose();
	};

	const renderAddNewOptions = () => {
		return (
			<Box>
				{newOptionNames.map((field, idx) => {
					const isDuplicate =
						currentOptionNames.includes(field) ||
						newOptionNames.filter((no, i) => !!no && i < idx).includes(field);
					return (
						<Box key={`${idx}_namevalue`} mt={2}>
							<TextField
								className={classNames(classes.input, classes.optionNameInput)}
								label={t('product.nameOfOption', 'Option name')}
								variant="outlined"
								value={field}
								onChange={handleNewOptionNameChange(idx)}
								error={isDuplicate}
								helperText={
									isDuplicate
										? t('product.optionAlreadyExists', 'This option name already exists')
										: undefined
								}
							/>
						</Box>
					);
				})}
			</Box>
		);
	};

	const renderNewVariantInputs = () => {
		const inputs = [...props.properties, ...newOptions];
		return (
			<Box>
				{inputs.map(({ id, name }, idx) => (
					<Box mt={2} key={`${id}_${idx}`} display="flex">
						<TextField
							className={classes.input}
							autoFocus={idx === 0 || (newOptionIds.includes(id) && idx === inputs.length - 1)}
							label={name.def}
							variant="outlined"
							error={!!errors[id]}
							helperText={errors?.[id]?.message}
							{...register(id)}
						/>
						{newOptionIds.includes(id) && (
							<Box className={classes.deleteButton}>
								<IconButton
									onClick={() =>
										setNewOptions((existing) => existing.filter(({ id: _id }) => id !== _id))
									}
								>
									<RiDeleteBinFill size={20} />
								</IconButton>
							</Box>
						)}
					</Box>
				))}
				<Box mt={1}>
					<Button variant="text" onClick={setAddOptionsActive}>
						+ {t('product.addOption', 'Add option')}
					</Button>
				</Box>
			</Box>
		);
	};

	return (
		<ActionDialog
			title={
				!isAddOptionsActive ? (
					t('product.variantsTable.newVariantTitle', 'New Variant')
				) : (
					<Box>
						<IconButton className={classes.modalBackArrow} size="small" onClick={cancelAddOptions}>
							<RiArrowLeftLine size={24} color="white" />
						</IconButton>
						{t('product.addOption', 'Add option')}
					</Box>
				)
			}
			headerVariant="contained"
			btnActions={{
				primary: {
					text: !isAddOptionsActive ? t('common:actions.save') : t('common:actions.done'),
					onClick: !isAddOptionsActive ? handleSubmit(handleSubmitForm) : submitNewOptions,
					disabled: isDialogPrimaryBtnDisabled,
				},
				secondary: {
					text: t('common:actions.cancel'),
					onClick: !isAddOptionsActive ? props.onClose : cancelAddOptions,
				},
			}}
			onClose={props.onClose}
		>
			<Box className={classNames({ [classes.hidden]: isAddOptionsActive })}>
				{renderNewVariantInputs()}
			</Box>
			<Box className={classNames({ [classes.hidden]: !isAddOptionsActive })}>
				{renderAddNewOptions()}
			</Box>
		</ActionDialog>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		input: {
			width: '100%',
		},
		optionNameInput: {
			marginRight: theme.spacing(1),
		},
		deleteButton: {
			marginLeft: theme.spacing(1),
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			maxHeight: theme.spacing(7),
		},
		modalBackArrow: {
			padding: 0,
			marginRight: theme.spacing(1),
		},
		hidden: {
			display: 'none',
		},
	}),
);

export default NewVariantDialog;
